import { useRoute, useRouter } from 'vue-router'

// Reads and writes task page state as query parameters, so any view a person
// is looking at is a link they can send to someone else.
//
// Two owners, because the state now lives on two pages: `useTasksUrlState` for
// /tasks (which tab, which checklist scope) and `useTaskStatsUrlState` for the
// task stats dashboard, which moved to /analytics/tasks. They share the same
// parsing rules so a link keeps working across the move.
//
// Precedence on load is URL, then localStorage, then defaults. A shared link
// therefore always wins over the recipient's own remembered preferences —
// otherwise opening someone's link would show you your dashboard, not theirs,
// which is the whole point of sending it.
//
// Deliberately does NOT write to localStorage when hydrating from a URL:
// opening a colleague's link should not silently overwrite your own saved
// filters. Your preferences are only rewritten once you touch a control
// yourself.

export const TABS = ['checklist', 'board']
export const CHECKLIST_SCOPES = ['today', 'week', 'overdue', 'someday', 'all']
export const STATS_RANGES = ['7d', '30d', '90d', '12m', 'custom']
export const STATS_TASK_TYPES = ['all', 'one_time', 'recurring']

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function pick(value, allowed) {
  return allowed.includes(value) ? value : null
}

// Comma separated rather than repeated `priority[]=` pairs: these end up in
// links people paste into chat, and `?priority=high,critical` survives that
// trip far better than `?priority%5B%5D=high&priority%5B%5D=critical`.
function splitList(value, allowed) {
  if (typeof value !== 'string' || !value) return null
  const parts = value.split(',').map(v => v.trim()).filter(v => allowed.includes(v))
  return parts.length ? parts : null
}

function joinList(values) {
  return values && values.length ? values.join(',') : undefined
}

function compact(object) {
  const out = {}
  Object.entries(object).forEach(([key, value]) => {
    if (value !== null && value !== undefined) out[key] = value
  })
  return Object.keys(out).length ? out : null
}

// Shared by both composables. Vue Router treats navigating to an identical
// location as a failure, so the equality guard is load bearing rather than an
// optimisation.
function useQueryWriter() {
  const route = useRoute()
  const router = useRouter()

  function sameAsCurrent(query) {
    const current = route.query
    const keys = new Set([...Object.keys(query), ...Object.keys(current)])
    return [...keys].every(k => String(query[k] ?? '') === String(current[k] ?? ''))
  }

  // `push` for a tab change, `replace` for a filter change. A tab is a place
  // you went, so Back should return you to the previous one. Filters are
  // adjustments to where you already are, and pushing each one would turn Back
  // into a tedious undo trail of every checkbox you touched.
  function write(query, { push = false } = {}) {
    const clean = Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined))
    if (sameAsCurrent(clean)) return

    const navigate = push ? router.push : router.replace
    navigate.call(router, { path: route.path, query: clean }).catch(() => {
      // A navigation cancelled by a newer one is normal here and not an error
      // worth surfacing.
    })
  }

  return { route, write }
}

// ── /tasks ────────────────────────────────────────────────────────────────
export function useTasksUrlState() {
  const { route, write } = useQueryWriter()

  // Everything is nullable: null means "the URL said nothing about this", which
  // is what lets the caller fall back to localStorage rather than stomping a
  // remembered value with a default.
  function readState() {
    const q = route.query
    return {
      tab: pick(q.tab, TABS),
      checklistScope: pick(q.scope, CHECKLIST_SCOPES),
    }
  }

  // Defaults are omitted so the common case stays a clean `/tasks`, and a link
  // only carries what actually differs from the norm.
  function writeState({ tab, checklistScope }, options = {}) {
    const q = {}
    if (tab && tab !== 'checklist') q.tab = tab
    if (tab === 'checklist' && checklistScope && checklistScope !== 'today') {
      q.scope = checklistScope
    }
    write(q, options)
  }

  return { readState, writeState }
}

// ── /analytics/tasks ──────────────────────────────────────────────────────
export function useTaskStatsUrlState() {
  const { route, write } = useQueryWriter()

  function readFilters(validColors = [], validPriorities = []) {
    const q = route.query

    const range = pick(q.range, STATS_RANGES)
    const from = DATE_RE.test(q.from || '') ? q.from : null
    const to = DATE_RE.test(q.to || '') ? q.to : null

    return compact({
      // A custom range needs both ends. One date alone would strand the page
      // on its "pick a start and end date" state with no visible cause.
      range: range === 'custom' && !(from && to) ? null : range,
      startDate: from,
      endDate: to,
      priorities: splitList(q.priority, validPriorities),
      colors: splitList(q.color, validColors),
      taskType: pick(q.type, STATS_TASK_TYPES),
      includeCanceled: q.canceled === '1' ? true : q.canceled === '0' ? false : null,
    })
  }

  function writeFilters(filters, options = {}) {
    const q = {}
    if (filters) {
      if (filters.range && filters.range !== '30d') q.range = filters.range
      if (filters.range === 'custom') {
        q.from = filters.startDate || undefined
        q.to = filters.endDate || undefined
      }
      q.priority = joinList(filters.priorities)
      q.color = joinList(filters.colors)
      if (filters.taskType && filters.taskType !== 'all') q.type = filters.taskType
      if (filters.includeCanceled) q.canceled = '1'
    }
    write(q, options)
  }

  return { readFilters, writeFilters }
}
